import SEO from "../components/SEO";
import { motion } from "framer-motion";
import { ArrowRight, Users, Star, Crown, CheckCircle, Home, TrendingUp, DollarSign, Shield, Building2, BarChart3, Briefcase, Menu, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

// Images
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/b3ade886c522cfe6ee077e87934fe7833a2266701e06a721f82feb4c47c098cb.png";
const TEAM_IMAGE = "https://images.unsplash.com/photo-1758691737492-48e8fdd336f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxleGNpdGVkJTIwaGFwcHklMjBlbXBsb3llZXMlMjBjZWxlYnJhdGluZyUyMHRlYW0lMjBzdWNjZXNzfGVufDB8fHx8MTc3Mjk3OTE3Nnww&ixlib=rb-4.1.0&q=85";
const HOMEOWNER_IMAGE = "https://images.unsplash.com/photo-1758523671285-9ff3f4e0ff38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNvdXBsZSUyMG5ldyUyMGhvbWUlMjBrZXlzJTIwbW92aW5nfGVufDB8fHx8MTc3Mjk3ODU0OXww&ixlib=rb-4.1.0&q=85";

const RetentionProgramsPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  const programs = [
    {
      icon: Users,
      name: "Base Retention",
      tagline: "Foundation Program",
      description: "Introduces homeownership support and resources to employees.",
      color: "blue",
      bgGradient: "from-blue-500 to-blue-600",
    },
    {
      icon: Star,
      name: "Premium Retention",
      tagline: "Company-Wide Support",
      description: "Provides down payment assistance for all full time employees.",
      color: "green",
      bgGradient: "from-green-500 to-green-600",
    },
    {
      icon: Crown,
      name: "Executive Retention",
      tagline: "Strategic Retention",
      description: "Provides targeted down payment assistance to retain key employees.",
      color: "purple",
      bgGradient: "from-purple-500 to-purple-600",
    }
  ];

  const baseIncludes = [
    "CityWorth All-in-One Homebuying Services",
    "Homebuyer education",
    "Homebuying rewards with thousands in savings",
    "Basic credit guidance to help employees qualify for a mortgage",
    "Company branded homeownership portal",
    "Employee Retention Analysis based on workforce and housing data",
    "Participation tracking and engagement reporting"
  ];

  const premiumIncludes = [
    "Employer funded down payment assistance",
    "Typical contributions of $5,000 to $15,000",
    "Funds used toward down payment or closing costs",
    "Available to all full time employees who qualify",
    "One-time lifetime benefit per employee",
    "No repayment required",
    "Program participation tracking",
    "Homeownership conversion reporting"
  ];

  const executiveIncludes = [
    "Employer funded down payment assistance for select employees",
    "Typical contributions of $15,000 to $50,000",
    "Used as a targeted retention incentive",
    "Stay agreement with defined employment period",
    "Retention tracking for participating employees"
  ];

  const homeownershipPath = [
    { step: "1", title: "Learn", desc: "Learn about the benefit through the company homeownership portal." },
    { step: "2", title: "Prepare", desc: "Review finances and prepare for mortgage qualification." },
    { step: "3", title: "Search", desc: "Search for homes with CityWorth real estate professionals." },
    { step: "4", title: "Finance", desc: "Secure financing and finalize the purchase." },
    { step: "5", title: "Close", desc: "Close on the home and begin building long term stability." }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white"
      data-testid="retention-programs-page"
    >
      <SEO title="Retention Programs" description="Explore CityWorth's Base, Premium, and Executive homeownership retention programs designed to help employers reduce turnover." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            
            <div className="relative">
              <Button 
                variant="outline" 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              <AnimatePresence>
                {isMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                    >
                      {menuItems.map((item, i) => (
                        <button
                          key={i}
                          onClick={item.action}
                          className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-8 pb-12 lg:pt-12 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-red-50/30" />
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Build a Workforce{" "}
                <span className="gradient-text">That Stays</span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-6">
                CityWorth helps companies increase retention by helping employees become homeowners.
              </p>

              <p className="text-lg text-slate-500 mb-6">
                Employees who own homes stay with their employer about <strong>3x longer</strong> than employees who rent.
              </p>

              <p className="text-lg text-slate-500 mb-8">
                Instead of constantly recruiting and replacing employees, companies can create long term stability by helping their people achieve homeownership.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/get-started">
                  <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Business team collaborating" 
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <motion.div 
                className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-lg border border-slate-100 hidden lg:flex"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">3x</div>
                    <div className="text-sm text-slate-500">Longer Retention</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Three Programs Overview */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              Retention{" "}
              <span className="gradient-text">Programs</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-4">
              CityWorth provides a structured approach to supporting employee homeownership.
            </p>
            <p className="text-lg text-slate-500 max-w-3xl mx-auto">
              Every company begins with the <strong>Base Retention Program</strong>, which introduces homeownership support and resources to employees. From there, companies can implement additional programs designed to strengthen retention.
            </p>
          </motion.div>

          {/* Program Cards - New Style */}
          <div className="max-w-5xl mx-auto space-y-6 mb-12">
            {/* Base Program - Full Width */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 border-2 border-blue-300 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                FOUNDATION
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-blue-800 text-xl mb-2">Base Retention</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    The foundation program that introduces homeownership support and gives employees the tools and guidance needed to prepare for purchasing a home.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {["Homebuyer education", "Homebuying rewards", "Credit guidance", "Branded portal"].map((item, j) => (
                      <span key={j} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <p className="text-slate-600 text-center font-medium py-2">
              Add additional programs for enhanced retention:
            </p>

            {/* Premium & Executive Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Premium */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-6 border-2 border-green-400 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  COMPANY-WIDE
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-green-700 text-xl mb-2">Premium Retention</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Down payment assistance available for all full time employees who qualify.
                    </p>
                    <div className="text-green-600 font-semibold text-sm">
                      $5,000 – $15,000 typical
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Executive */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 border-2 border-purple-400 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  STRATEGIC
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-purple-700 text-xl mb-2">Executive Retention</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Targeted down payment assistance used to retain key employees and leadership.
                    </p>
                    <div className="text-purple-600 font-semibold text-sm">
                      $15,000 – $50,000 typical
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <p className="text-center text-slate-600 text-lg">
            Companies may implement one or both Premium and Executive approaches depending on their goals.
          </p>
        </div>
      </section>

      {/* Base Retention Program */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Foundation Program</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Base Retention{" "}
                <span className="text-blue-600">Program</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-8">
                The Base program introduces homeownership support and gives employees the tools and guidance needed to prepare for purchasing a home.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <h4 className="font-bold text-slate-900 mb-2">Purpose</h4>
                <p className="text-slate-700">
                  Help employees become homeowners while introducing a benefit that supports long term employee stability.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm"
            >
              <h3 className="text-xl font-bold text-slate-900 mb-6">Includes</h3>
              <div className="space-y-3">
                {baseIncludes.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Premium Retention Program */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:order-2"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-100 mb-6">
                <Star className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Company-Wide Support</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Premium Retention{" "}
                <span className="text-green-600">Program</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-8">
                The Premium program provides down payment assistance for all full time employees who qualify to purchase a home. This program strengthens company culture while helping organizations recruit and retain employees.
              </p>

              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h4 className="font-bold text-slate-900 mb-2">Purpose</h4>
                <p className="text-slate-700">
                  Support employees across the organization in purchasing homes while strengthening recruiting, culture, and retention.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm lg:order-1"
            >
              <h3 className="text-xl font-bold text-slate-900 mb-6">Includes</h3>
              <div className="space-y-3">
                {premiumIncludes.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Executive Retention Program */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-100 mb-6">
                <Crown className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Strategic Retention</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Executive Retention{" "}
                <span className="text-purple-600">Program</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-8">
                The Executive program provides targeted down payment assistance for key employees and leadership. Companies use this program as a strategic retention tool for important team members.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                <h4 className="font-bold text-slate-900 mb-2">Purpose</h4>
                <p className="text-slate-700">
                  Provide a focused retention strategy for high value employees the company wants to retain long term.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm"
            >
              <h3 className="text-xl font-bold text-slate-900 mb-6">Includes</h3>
              <div className="space-y-3">
                {executiveIncludes.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Homeownership Changes Behavior */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Homeownership Changes{" "}
                <span className="text-yellow-400">Employee Behavior</span>
              </h2>
              
              <p className="text-lg text-white/80 mb-6">
                Homeownership is one of the biggest financial milestones in a person's life. When employees purchase a home, it often signals a shift toward long term stability.
              </p>

              <p className="text-lg text-white/80 mb-6">
                Homeowners tend to build deeper financial roots, establish stronger community ties, and focus more on long term career growth.
              </p>

              <p className="text-lg text-white/80">
                Because of this stability, employees who own homes are often less likely to change jobs frequently compared to those who rent. This is one reason workforce data consistently shows that employees who own homes stay significantly longer than renters.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-2xl"
            >
              <img 
                src={HOMEOWNER_IMAGE} 
                alt="Happy couple in new home" 
                className="w-full h-80 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Retention Problem */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              The Retention Problem{" "}
              <span className="gradient-text">Most Companies Are Missing</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-red-50 rounded-2xl p-8 border border-red-100"
            >
              <DollarSign className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-4">The True Cost of Turnover</h3>
              <p className="text-slate-700 mb-4">
                Turnover comes with real costs including recruiting, hiring, onboarding, and lost productivity.
              </p>
              <p className="text-slate-700">
                In many cases, replacing an employee can cost <strong>1 to 2 times their annual salary</strong> when recruiting expenses, training time, lost institutional knowledge, and reduced productivity are considered.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-blue-50 rounded-2xl p-8 border border-blue-100"
            >
              <Users className="w-12 h-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-4">The Ripple Effect</h3>
              <p className="text-slate-700 mb-4">
                Turnover can also have a ripple effect across teams. When employees leave, it can disrupt morale and sometimes influence others to begin exploring new opportunities.
              </p>
              <p className="text-slate-700 font-semibold">
                Helping employees achieve homeownership can strengthen long term workforce stability.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Homeownership Path */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              The Homeownership{" "}
              <span className="gradient-text">Path</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              CityWorth provides employees with a clear path to homeownership.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-4 mb-8">
            {homeownershipPath.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm text-center h-full">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-blue-500 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                    {item.step}
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
                {i < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-600 text-lg">
            For companies offering Premium or Executive programs, down payment assistance may be applied toward the purchase.
          </p>
        </div>
      </section>

      {/* Employee Retention Analysis */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Data-Driven Insights</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Employee Retention{" "}
                <span className="gradient-text">Analysis</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-6">
                CityWorth provides companies with a detailed Employee Retention Analysis that measures the relationship between homeownership and employee tenure.
              </p>

              <p className="text-lg text-slate-600 mb-8">
                Using employee address data and workforce tenure data, CityWorth's proprietary system determines:
              </p>

              <div className="space-y-3">
                {[
                  "Which employees are homeowners",
                  "Which employees rent",
                  "The median tenure difference between the two groups"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-lg text-slate-700 mt-6 font-medium">
                This allows companies to clearly see how increasing employee homeownership could impact retention.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img 
                src={TEAM_IMAGE} 
                alt="Professional team" 
                className="w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-blue-800" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-yellow-400/20 via-transparent to-transparent" />
        
        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Get Your Retention Analysis
            </h2>
            <p className="text-xl text-white/90 mb-10">
              CityWorth can analyze your workforce data to estimate the potential retention impact of helping more employees become homeowners.
            </p>
            <Link to="/get-started">
              <Button 
                size="lg" 
                className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-6 py-4 md:px-10 md:py-6 text-base md:text-xl font-bold shadow-2xl shadow-yellow-400/30 hover:shadow-yellow-400/50 transition-all hover:scale-105"
              >
                Request Your Analysis
                <ArrowRight className="ml-2 w-5 h-5 md:w-6 md:h-6" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/retention-programs" className="text-slate-600 hover:text-slate-900">Programs</Link>
            <Link to="/retention-analysis" className="text-slate-600 hover:text-slate-900">Analysis</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
            <Link to="/careers" className="text-slate-600 hover:text-slate-900">Careers</Link>
          </div>
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </motion.div>
  );
};

export default RetentionProgramsPage;
